/**
 * reportData - dummy data + helpers for the Report Builder demo.
 * Phase D will replace this with a real backend call.
 */

export const CATEGORY_PACKS = [
  { id: "fan-deep-dive",     title: "Fan deep-dive",     desc: "Acquisition, engagement, retention, top spenders",  accent: "#FC8019", icon: "F", defaultSections: ["fan-summary", "fan-trend", "fan-source", "fan-tier", "top-fans"] },
  { id: "creator-deep-dive", title: "Creator deep-dive", desc: "Onboarding funnel, earnings, at-risk, top performers", accent: "#7C4DFF", icon: "C", defaultSections: ["creator-summary", "creator-trend", "creator-tier", "top-creators", "creator-funnel"] },
  { id: "brand-deep-dive",   title: "Brand deep-dive",   desc: "Pipeline, campaigns, spend, top brands",            accent: "#4CAF50", icon: "B", defaultSections: ["brand-summary", "brand-trend", "brand-vertical", "brand-funnel", "top-brands"] },
  { id: "money",             title: "Money deep-dive",   desc: "Revenue, payouts, GST, TDS, cashflow",              accent: "#FF6B9D", icon: "M", defaultSections: ["money-summary", "revenue-trend", "revenue-mix", "payouts-mix", "tax"] },
  { id: "operations",        title: "Operations",        desc: "Calls, support, refunds, call quality",             accent: "#FC8019", icon: "O", defaultSections: ["ops-summary", "calls-trend", "calls-by-day", "outcomes"] },
  { id: "board-pack",        title: "Quarterly board pack", desc: "12-page CEO summary for board meeting",          accent: "#7C4DFF", icon: "Q", defaultSections: ["fan-summary", "creator-summary", "brand-summary", "money-summary", "revenue-trend", "revenue-mix", "top-creators", "top-brands"] },
];

// Provenance metadata per section. Documents which DB tables the data is read from,
// the rough query (what gets filtered by date), and how many rows were summarised.
// Mirrors the real read-only Postgres structure in docs/DATABASE.md.
export const SECTION_SOURCES = {
  "fan-summary":      { tables: ["users (Postgres)"],                                          query: "users WHERE role_id = 2 (fan), filtered by registered_at AT TIME ZONE 'Asia/Kolkata'", rows: 102000 },
  "fan-trend":        { tables: ["users (Postgres)"],                                          query: "Monthly count of users WHERE role_id = 2, IST month bucket",                              rows: 12 },
  "fan-source":       { tables: ["users (Postgres)"],                                          query: "users WHERE role_id = 2 GROUP BY signup_source",                                          rows: 5 },
  "fan-tier":         { tables: ["users (Postgres)", "callhistory (Postgres)"],                query: "Active fans grouped by lifetime call count bucket",                                       rows: 4 },
  "top-fans":         { tables: ["transaction_history (Postgres)", "users (Postgres)"],        query: "SUM(amount) per fan WHERE type = 'fan-add-credit' AND is_paid = 'success'; ORDER BY 1 DESC LIMIT 10", rows: 10 },
  "creator-summary":  { tables: ["users (Postgres)", "callhistory (Postgres)"],                query: "users WHERE role_id = 3 (celebrity); active = had call in last 30d",                      rows: 8540 },
  "creator-trend":    { tables: ["users (Postgres)"],                                          query: "Monthly count of users WHERE role_id = 3, IST month bucket",                              rows: 12 },
  "creator-tier":     { tables: ["callhistory (Postgres)", "transaction_history (Postgres)"],  query: "Creators grouped by earning tier (top / mid / low / at-risk / dormant)",                  rows: 5 },
  "creator-funnel":   { tables: ["users", "callhistory", "transaction_history", "brand_campaigns"], query: "First-event-per-creator across signup, first call received, first brand deal, first withdrawal", rows: 4 },
  "top-creators":     { tables: ["transaction_history (Postgres)", "users (Postgres)"],        query: "SUM(amount) per creator WHERE type = 'youtuber-earning'; ORDER BY 1 DESC LIMIT 10",       rows: 10 },
  "brand-summary":    { tables: ["users (Postgres)", "brand_campaigns (Postgres)"],            query: "users WHERE role_id = 4 (brand) + open campaigns count",                                   rows: 142 },
  "brand-trend":      { tables: ["brand_transaction_history (Postgres)"],                      query: "SUM(amount) per IST month WHERE transaction_type = 'campaign_payment' AND payment_status = 'success'", rows: 12 },
  "brand-vertical":   { tables: ["users (Postgres)"],                                          query: "users WHERE role_id = 4 GROUP BY brand_vertical",                                          rows: 6 },
  "brand-funnel":     { tables: ["users", "brand_transaction_history", "brand_campaigns"],     query: "First-event-per-brand across signup, first topup, first campaign, repeat campaign",       rows: 4 },
  "top-brands":       { tables: ["brand_transaction_history (Postgres)", "users (Postgres)"],  query: "SUM(amount) per brand WHERE transaction_type IN ('campaign_payment','wallet_deposit'); ORDER BY 1 DESC LIMIT 10", rows: 10 },
  "money-summary":    { tables: ["transaction_history", "brand_transaction_history"],         query: "Today + MTD totals across both transaction tables WHERE is_paid = 'success' AND IST date filter", rows: 4 },
  "revenue-trend":    { tables: ["transaction_history", "brand_transaction_history"],         query: "SUM(amount) per IST month, both tables combined WHERE is_paid = 'success'",               rows: 12 },
  "revenue-mix":      { tables: ["transaction_history", "brand_transaction_history", "callhistory"], query: "MTD revenue split by source: fan calls, brand campaigns, wallet topups",         rows: 3 },
  "payouts-mix":      { tables: ["transaction_history (Postgres)"],                            query: "MTD outflows split by type: youtuber-earning, brand refunds, promo, withdrawals",          rows: 4 },
  "tax":              { tables: ["transaction_history", "brand_transaction_history"],         query: "MTD GST = 18% of paid revenue (CGST 50% + SGST 50%); TDS = 10% of creator earnings",       rows: 3 },
  "ops-summary":      { tables: ["callhistory (Postgres)"],                                    query: "Today: count + duration + failure rate + live count from callhistory.status",              rows: 4 },
  "calls-trend":      { tables: ["callhistory (Postgres)"],                                    query: "Count(*) per IST month FROM callhistory WHERE status = 'call-completed'",                  rows: 12 },
  "calls-by-day":     { tables: ["callhistory (Postgres)"],                                    query: "Count(*) per IST day-of-week FROM callhistory for current week",                          rows: 7 },
  "outcomes":         { tables: ["callhistory (Postgres)"],                                    query: "Count(*) GROUP BY status FROM callhistory for IST today",                                  rows: 5 },
};

// Compute concrete from/to dates (YYYY-MM-DD) for a range id.
// Approximated to local time here; backend computes the real IST-anchored values.
export function computeDateBounds(rangeId, customRange) {
  if (rangeId === "custom" && customRange && customRange.from && customRange.to) {
    return { from: customRange.from, to: customRange.to };
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return y + "-" + m + "-" + day;
  };
  switch (rangeId) {
    case "today":      return { from: iso(today), to: iso(today) };
    case "this-week": {
      const dow = today.getDay(); // Sun=0
      const monday = new Date(today); monday.setDate(today.getDate() - ((dow + 6) % 7));
      return { from: iso(monday), to: iso(today) };
    }
    case "this-month": return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
    case "last-month": {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last  = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: iso(first), to: iso(last) };
    }
    case "this-year":  return { from: iso(new Date(today.getFullYear(), 0, 1)), to: iso(today) };
    case "last-year":  return { from: (today.getFullYear() - 1) + "-01-01", to: (today.getFullYear() - 1) + "-12-31" };
    case "till-date":  return { from: "2021-01-15", to: iso(today) };
    default:           return { from: iso(today), to: iso(today) };
  }
}

export const ALL_SECTIONS = {
  "fan-summary":     { id: "fan-summary",     group: "Fans",       title: "Fan KPIs",                kind: "kpi" },
  "fan-trend":       { id: "fan-trend",       group: "Fans",       title: "Total fans - 12 months",  kind: "trend" },
  "fan-source":      { id: "fan-source",      group: "Fans",       title: "Fans by source",          kind: "hbars" },
  "fan-tier":        { id: "fan-tier",        group: "Fans",       title: "Fan engagement tiers",    kind: "donut" },
  "top-fans":        { id: "top-fans",        group: "Fans",       title: "Top 10 fans by spend",    kind: "leaderboard" },
  "creator-summary": { id: "creator-summary", group: "Creators",   title: "Creator KPIs",            kind: "kpi" },
  "creator-trend":   { id: "creator-trend",   group: "Creators",   title: "Total creators - 12 months", kind: "trend" },
  "creator-tier":    { id: "creator-tier",    group: "Creators",   title: "Creators by tier",        kind: "hbars" },
  "creator-funnel":  { id: "creator-funnel",  group: "Creators",   title: "Creator funnel",          kind: "funnel" },
  "top-creators":    { id: "top-creators",    group: "Creators",   title: "Top 10 creators",         kind: "leaderboard" },
  "brand-summary":   { id: "brand-summary",   group: "Brands",     title: "Brand KPIs",              kind: "kpi" },
  "brand-trend":     { id: "brand-trend",     group: "Brands",     title: "Brand revenue - 12 months", kind: "trend" },
  "brand-vertical":  { id: "brand-vertical",  group: "Brands",     title: "Brands by vertical",      kind: "hbars" },
  "brand-funnel":    { id: "brand-funnel",    group: "Brands",     title: "Brand funnel",            kind: "funnel" },
  "top-brands":      { id: "top-brands",      group: "Brands",     title: "Top 10 brands",           kind: "leaderboard" },
  "money-summary":   { id: "money-summary",   group: "Money",      title: "Money KPIs",              kind: "kpi" },
  "revenue-trend":   { id: "revenue-trend",   group: "Money",      title: "Revenue - 12 months",     kind: "trend" },
  "revenue-mix":     { id: "revenue-mix",     group: "Money",      title: "Revenue mix",             kind: "donut" },
  "payouts-mix":     { id: "payouts-mix",     group: "Money",      title: "Payouts mix",             kind: "donut" },
  "tax":             { id: "tax",             group: "Money",      title: "Tax breakdown",           kind: "hbars" },
  "ops-summary":     { id: "ops-summary",     group: "Operations", title: "Operations KPIs",         kind: "kpi" },
  "calls-trend":     { id: "calls-trend",     group: "Operations", title: "Calls - 12 months",      kind: "trend" },
  "calls-by-day":    { id: "calls-by-day",    group: "Operations", title: "Calls by day - week",    kind: "bars" },
  "outcomes":        { id: "outcomes",        group: "Operations", title: "Call outcomes",          kind: "donut" },
};

export const formatINR = (n) => {
  if (typeof n !== "number") return n;
  if (n >= 1e7) return "Rs " + (n / 1e7).toFixed(2) + "Cr";
  if (n >= 1e5) return "Rs " + (n / 1e5).toFixed(2) + "L";
  if (n >= 1e3) return "Rs " + (n / 1e3).toFixed(1) + "K";
  return "Rs " + n;
};
export const formatNum = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : n);

const MONTHS = ["May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr"];
function trendSeries(start, end) {
  const out = [];
  for (let i = 0; i < 12; i++) {
    const t = i / 11;
    const v = Math.round(start + (end - start) * t + Math.sin(i * 0.7) * (end - start) * 0.05);
    out.push({ label: MONTHS[i], value: v });
  }
  return out;
}

export function buildSectionData(id) {
  switch (id) {
    case "fan-summary": return { kind: "kpi", format: formatNum, kpis: [
      { label: "Total fans", value: 102000, sub: "+120 today" },
      { label: "Active this month", value: 74200, sub: "72.7% retention" },
      { label: "Paying fans", value: 38400, sub: "37.6% conversion" },
      { label: "VIP fans (21+ calls)", value: 13800, sub: "13.5% of total" },
    ] };
    case "fan-trend": return { kind: "trend", format: formatNum, series: trendSeries(71200, 102000) };
    case "fan-source": return { kind: "hbars", format: formatNum, rows: [
      { label: "Organic search", value: 48200 }, { label: "Referral", value: 22400 },
      { label: "Instagram", value: 14800 }, { label: "YouTube", value: 8900 }, { label: "Other", value: 7700 },
    ] };
    case "fan-tier": return { kind: "donut", format: formatNum, center: { label: "Active", value: 102000 }, segments: [
      { label: "First-call only", value: 38400 }, { label: "2-5 calls", value: 28200 },
      { label: "6-20 calls", value: 21600 }, { label: "21+ calls (VIP)", value: 13800 },
    ] };
    case "top-fans": return { kind: "leaderboard", format: formatINR, rightLabel: "Spend", rows: [
      { name: "Ravi Kumar", value: 284000 }, { name: "Meera Nair", value: 246000 },
      { name: "Aditya Singh", value: 212000 }, { name: "Pooja Desai", value: 188000 },
      { name: "Sahil Khan", value: 165000 }, { name: "Aisha Rao", value: 142000 },
      { name: "Vikram Joshi", value: 128000 }, { name: "Neha Bansal", value: 112000 },
      { name: "Arjun Malhotra", value: 98000 }, { name: "Tara Mehta", value: 84000 },
    ] };
    case "creator-summary": return { kind: "kpi", format: formatNum, kpis: [
      { label: "Total creators", value: 8540, sub: "+24 this week" },
      { label: "Active this month", value: 5840, sub: "68.4% retention" },
      { label: "Earning today", value: 417, sub: "got paid today" },
      { label: "At-risk creators", value: 226, sub: "no calls in 30d" },
    ] };
    case "creator-trend": return { kind: "trend", format: formatNum, series: trendSeries(6850, 8540) };
    case "creator-tier": return { kind: "hbars", format: formatNum, rows: [
      { label: "Top earners", value: 320 }, { label: "Mid", value: 1580 },
      { label: "Low / new", value: 4080 }, { label: "At-risk (30d)", value: 226 },
      { label: "Dormant (90d+)", value: 2334 },
    ] };
    case "creator-funnel": return { kind: "funnel", format: formatNum, stages: [
      { label: "Creator signup completed", value: 168 },
      { label: "First call received", value: 92 },
      { label: "First brand deal received", value: 28 },
      { label: "First withdrawal", value: 18 },
    ] };
    case "top-creators": return { kind: "leaderboard", format: formatINR, rightLabel: "Earnings", rows: [
      { name: "Aarav Sharma", value: 184500 }, { name: "Priya Patel", value: 152300 },
      { name: "Rohan Kapoor", value: 138900 }, { name: "Sneha Iyer", value: 119600 },
      { name: "Karan Mehta", value: 102100 }, { name: "Anjali Verma", value: 92400 },
      { name: "Vikas Reddy", value: 81700 }, { name: "Ishaan Bose", value: 74200 },
      { name: "Tanvi Shah", value: 68800 }, { name: "Devansh Gupta", value: 61400 },
    ] };
    case "brand-summary": return { kind: "kpi", format: formatNum, kpis: [
      { label: "Total brands", value: 142, sub: "+1 today" },
      { label: "Active this month", value: 38, sub: "26.8% activation" },
      { label: "Brand requests today", value: 24, sub: "to creators" },
      { label: "Brand-creator calls", value: 12, sub: "held today" },
    ] };
    case "brand-trend": return { kind: "trend", format: formatINR, series: trendSeries(380000, 840000) };
    case "brand-vertical": return { kind: "hbars", format: formatNum, rows: [
      { label: "Food & beverage", value: 32 }, { label: "Fashion", value: 28 },
      { label: "Tech / SaaS", value: 24 }, { label: "Beauty", value: 18 },
      { label: "Entertainment", value: 14 }, { label: "Other", value: 26 },
    ] };
    case "brand-funnel": return { kind: "funnel", format: formatNum, stages: [
      { label: "Brand signup completed", value: 38 },
      { label: "First wallet topup", value: 26 },
      { label: "First campaign created", value: 18 },
      { label: "Next campaign created", value: 11 },
    ] };
    case "top-brands": return { kind: "leaderboard", format: formatINR, rightLabel: "Spend", rows: [
      { name: "Hindustan Unilever", value: 1840000 }, { name: "Tata CLiQ", value: 1520000 },
      { name: "Boat Lifestyle", value: 1380000 }, { name: "MyGlamm", value: 1190000 },
      { name: "Mamaearth", value: 1020000 }, { name: "CRED", value: 920000 },
      { name: "Razorpay", value: 810000 }, { name: "Nykaa", value: 720000 },
      { name: "Zomato", value: 640000 }, { name: "Swiggy", value: 580000 },
    ] };
    case "money-summary": return { kind: "kpi", format: formatINR, kpis: [
      { label: "Revenue today", value: 342000, sub: "fan + brand" },
      { label: "Wallet topups today", value: 184000, sub: "fan + brand wallets" },
      { label: "Pending payouts", value: 420000, sub: "142 creators" },
      { label: "GST collected (MTD)", value: 162000, sub: "CGST + SGST" },
    ] };
    case "revenue-trend": return { kind: "trend", format: formatINR, series: trendSeries(1820000, 3420000) };
    case "revenue-mix": return { kind: "donut", format: formatINR, center: { label: "Total", value: 342000 }, segments: [
      { label: "Fan calls", value: 215000 }, { label: "Brand campaigns", value: 84000 }, { label: "Wallet topups", value: 43000 },
    ] };
    case "payouts-mix": return { kind: "donut", format: formatINR, center: { label: "Out", value: 420000 }, segments: [
      { label: "Creator earnings", value: 320000 }, { label: "Brand refunds", value: 48000 },
      { label: "Promo credits", value: 22000 }, { label: "Withdrawals", value: 30000 },
    ] };
    case "tax": return { kind: "hbars", format: formatINR, rows: [
      { label: "CGST collected", value: 81000 }, { label: "SGST collected", value: 81000 }, { label: "TDS withheld", value: 142000 },
    ] };
    case "ops-summary": return { kind: "kpi", format: formatNum, kpis: [
      { label: "Calls today", value: 1250, sub: "4,820 minutes" },
      { label: "Live now", value: 23, sub: "18 fan + 4 brand + 1 room" },
      { label: "Failure rate", value: "2.4%", sub: "30 of 1,250" },
      { label: "Avg call duration", value: "3.8 min", sub: "vs 3.6 last wk" },
    ] };
    case "calls-trend": return { kind: "trend", format: formatNum, series: trendSeries(18200, 35200) };
    case "calls-by-day": return { kind: "bars", format: formatNum, rows: [
      { label: "Mon", value: 820 }, { label: "Tue", value: 1100 },
      { label: "Wed", value: 1320 }, { label: "Thu", value: 980 },
      { label: "Fri", value: 1480 }, { label: "Sat", value: 1820 },
      { label: "Sun", value: 1250 },
    ] };
    case "outcomes": return { kind: "donut", format: formatNum, center: { label: "Total", value: 1250 }, segments: [
      { label: "Completed", value: 1042 }, { label: "Cancelled (fan)", value: 124 },
      { label: "No-show creator", value: 38 }, { label: "Tech failure", value: 22 }, { label: "Refunded", value: 24 },
    ] };
    default: return null;
  }
}

export function buildNarrative(sectionIds, rangeLabel) {
  const lines = ["Period covered: " + rangeLabel + "."];
  if (sectionIds.includes("revenue-trend") || sectionIds.includes("money-summary")) {
    lines.push("Revenue this month is Rs 34.20L, up 12.5% from Rs 30.40L last month. Highest month YTD: March 2026 at Rs 35.80L (+88% YoY).");
  }
  if (sectionIds.includes("fan-summary") || sectionIds.includes("fan-trend")) {
    lines.push("Fan base grew to 1,02,000 (up 43% YoY from 71,200). 38 new first-time payers today vs 12 yesterday (+216%).");
  }
  if (sectionIds.includes("creator-summary") || sectionIds.includes("creator-trend")) {
    lines.push("8,540 creators on platform; 417 earned today. 226 at-risk (no calls in 30 days), down 7.5% week-over-week.");
  }
  if (sectionIds.includes("brand-summary") || sectionIds.includes("brand-trend")) {
    lines.push("Brand revenue Rs 8.40L this month, up 121% YoY. 12 brand-creator calls held today across 24 brand requests.");
  }
  if (sectionIds.includes("revenue-mix")) {
    lines.push("Revenue mix: fan calls 62.9%, brand campaigns 24.6%, wallet topups 12.6%. Brand-campaign share grew 3pp vs Q1.");
  }
  if (sectionIds.includes("ops-summary") || sectionIds.includes("calls-trend")) {
    lines.push("35,200 calls this month (down 4.3% vs March's 36,800 peak). 4.6/5 avg call rating, 1.8% drop rate.");
  }
  if (sectionIds.includes("creator-funnel") || sectionIds.includes("brand-funnel")) {
    lines.push("Creator funnel end-to-end: 10.7% (signup -> first withdrawal). Brand funnel: 28.9% (signup -> repeat campaign).");
  }
  return lines.slice(0, 6);
}

export function buildCSV(sectionIds, rangeLabel, narrative, opts) {
  const o = opts || {};
  const bounds = o.bounds || { from: "", to: "" };
  const generatedBy = o.generatedBy || "Jasmin T (CEO)";
  const generatedAt = new Date().toLocaleString();
  // STRICT: never include DB tables / SQL / row counts / backend hints in production.
  // Defaults to FALSE (provenance hidden) unless caller explicitly opts in via opts.includeProvenance.
  const provenance = o.includeProvenance === true;
  const lines = [];

  // ── Cover ──────────────────────────────────────────────────────────
  lines.push(["FANCALL DASHBOARD REPORT"]);
  lines.push(["Title",            o.title || "Custom report"]);
  lines.push(["Range (label)",    rangeLabel]);
  lines.push(["Range (from)",     bounds.from]);
  lines.push(["Range (to)",       bounds.to]);
  lines.push(["Timezone",         "Asia/Kolkata (IST)"]);
  lines.push(["Currency",         "INR (Rs)"]);
  lines.push(["Number format",    "Indian (e.g. 1,02,000)"]);
  lines.push(["Generated at",     generatedAt]);
  lines.push(["Generated by",     generatedBy]);
  lines.push(["Sections included", sectionIds.length]);
  lines.push([]);

  // ── Methodology ────────────────────────────────────────────────────
  lines.push(["METHODOLOGY & DEFINITIONS"]);
  lines.push(["First-action-today", "An actor counts as a first-action-today only if it is their first-ever such event AND it happened today in IST."]);
  lines.push(["Read-only data",     "All Postgres reads use a SELECT-only role. No mutations of any kind."]);
  lines.push(["Refund rate",        "Calls refunded / calls placed in the period."]);
  lines.push(["At-risk creator",   "Creator with no calls received in the last 30 IST days."]);
  lines.push(["Active fan",        "Fan with >= 1 successful call in the period."]);
  lines.push(["GST",               "18% of paid revenue (split CGST 50% / SGST 50%)."]);
  lines.push(["TDS",               "10% withheld on creator earnings above Rs 30,000 / fiscal year."]);
  lines.push([]);

  // ── Narrative summary ──────────────────────────────────────────────
  lines.push(["NARRATIVE SUMMARY"]);
  narrative.forEach((s) => lines.push([s]));
  lines.push([]);

  // ── Each section ───────────────────────────────────────────────────
  sectionIds.forEach((id, idx) => {
    const meta = ALL_SECTIONS[id];
    const data = buildSectionData(id);
    const src  = SECTION_SOURCES[id] || {};
    if (!meta || !data) return;

    lines.push([`SECTION ${idx + 1}: ${meta.title}`]);
    lines.push(["Group",         meta.group]);
    if (provenance) {
      lines.push(["Source tables", (src.tables || []).join(" + ")]);
      lines.push(["Query",         src.query || ""]);
      lines.push(["Rows",          src.rows != null ? src.rows : ""]);
    }
    lines.push(["Range applied", `${bounds.from} -> ${bounds.to} (IST)`]);
    lines.push(["Refreshed at",  generatedAt]);
    lines.push([]);

    if (data.kind === "kpi") {
      lines.push(["Metric", "Value", "Note"]);
      data.kpis.forEach((k) => lines.push([k.label, k.value, k.sub || ""]));
    } else if (data.kind === "trend") {
      lines.push(["Period (IST month)", "Value"]);
      data.series.forEach((p) => lines.push([p.label, p.value]));
    } else if (data.kind === "hbars" || data.kind === "bars") {
      const total = data.rows.reduce((s, r) => s + (r.value || 0), 0);
      lines.push(["Label", "Value", "% of total"]);
      data.rows.forEach((r) => lines.push([r.label, r.value, total ? ((r.value / total) * 100).toFixed(2) + "%" : ""]));
      lines.push(["Total", total, "100%"]);
    } else if (data.kind === "donut") {
      const total = data.segments.reduce((s, x) => s + (x.value || 0), 0);
      lines.push(["Segment", "Value", "% of total"]);
      data.segments.forEach((s) => lines.push([s.label, s.value, total ? ((s.value / total) * 100).toFixed(2) + "%" : ""]));
      lines.push(["Total", total, "100%"]);
    } else if (data.kind === "leaderboard") {
      lines.push(["Rank", "Name", data.rightLabel || "Value"]);
      data.rows.forEach((r, i) => lines.push([i + 1, r.name, r.value]));
    } else if (data.kind === "funnel") {
      const max = data.stages[0].value;
      lines.push(["Stage", "Count", "% of top", "Step conversion"]);
      data.stages.forEach((s, i) => {
        const pctTop = ((s.value / max) * 100).toFixed(2) + "%";
        const step = i === 0 ? "—" : ((s.value / data.stages[i - 1].value) * 100).toFixed(2) + "%";
        lines.push([s.label, s.value, pctTop, step]);
      });
    }
    lines.push([]);
  });

  // ── Footer ─────────────────────────────────────────────────────────
  lines.push(["END OF REPORT"]);
  lines.push(["Questions",  "fancalldev@gmail.com"]);
  if (provenance) {
    lines.push(["Backend",    "Read-only Postgres + AST query validator + boot canary"]);
    lines.push(["Auth DB",    "SQLite (separate from analytics DB) - the only writable store"]);
  }
  lines.push([]);

  return lines.map((row) =>
    row.map((cell) => {
      const s = String(cell == null ? "" : cell);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }).join(",")
  ).join("\n");
}
